import type { Capability, CaseStudy, Package, Testimonial } from './types';

export const capabilities: Capability[] = [
  {
    id: 'web',
    number: '01',
    title: 'Sitios comerciales animados',
    description: 'Narrativas digitales que ordenan la atención, explican valor y convierten sin sentirse como una plantilla.',
    tags: ['Dirección de arte', 'Motion', 'Conversión'],
    metric: 'Scroll con intención',
  },
  {
    id: '3d',
    number: '02',
    title: 'Experiencias 3D de marca',
    description: 'Objetos, espacios y sistemas visuales interactivos que vuelven tangible una propuesta difícil de explicar.',
    tags: ['WebGL', 'Producto', 'Inmersión'],
    metric: 'Profundidad real',
  },
  {
    id: 'flows',
    number: '03',
    title: 'Cotizadores y flujos guiados',
    description: 'Herramientas que transforman una visita difusa en una consulta calificada con contexto comercial.',
    tags: ['UX', 'Reglas locales', 'Leads'],
    metric: 'Menos fricción',
  },
  {
    id: 'launch',
    number: '04',
    title: 'Lanzamientos y micrositios',
    description: 'Una puesta en escena digital para desarrollos, productos, aperturas, eventos y campañas de alto valor.',
    tags: ['Campaña', 'Storytelling', 'Datos'],
    metric: 'Máxima señal',
  },
  {
    id: 'commerce',
    number: '05',
    title: 'Catálogos interactivos',
    description: 'Exploración visual, filtros inteligentes y recorridos editoriales para productos que merecen más que una grilla.',
    tags: ['Retail', 'Ecommerce', 'Exploración'],
    metric: 'Deseo + claridad',
  },
  {
    id: 'systems',
    number: '06',
    title: 'Sistemas comerciales livianos',
    description: 'Reservas, consultas, dashboards y automatizaciones diseñadas alrededor de cómo opera el negocio.',
    tags: ['Reservas', 'Dashboards', 'Operación'],
    metric: 'Trabajo conectado',
  },
];

export const packages: Package[] = [
  {
    name: 'Señal',
    eyebrow: 'Presencia premium',
    price: 'ARS 2,8M — 4,2M',
    timeline: '4 a 6 semanas',
    ideal: 'Marcas que necesitan dejar atrás una web correcta y construir una presencia propia.',
    features: ['Hasta 6 secciones', 'Dirección visual', 'Motion editorial', 'SEO base', 'Formulario comercial'],
  },
  {
    name: 'Pulso',
    eyebrow: 'Conversión animada',
    price: 'ARS 4,8M — 7,5M',
    timeline: '6 a 9 semanas',
    ideal: 'Servicios premium con una decisión de compra que necesita guía y argumento.',
    features: ['Narrativa de conversión', 'Cotizador o flujo guiado', 'Animación avanzada', 'Analítica preparada', 'Sistema de leads'],
    featured: true,
  },
  {
    name: 'Campo',
    eyebrow: 'Experiencia inmersiva',
    price: 'ARS 8,5M — 14M',
    timeline: '9 a 13 semanas',
    ideal: 'Lanzamientos, real estate, hospitality y marcas donde la percepción define el valor.',
    features: ['WebGL / 3D', 'Storytelling inmersivo', 'Interacciones a medida', 'Performance adaptativa', 'Prototipo de alta fidelidad'],
  },
  {
    name: 'Órbita',
    eyebrow: 'Lanzamiento integral',
    price: 'Desde ARS 16M',
    timeline: '12 a 18 semanas',
    ideal: 'Equipos que necesitan concepto, experiencia, sistema comercial y activación coordinados.',
    features: ['Estrategia y concepto', 'Ecosistema multipágina', '3D + motion', 'Flujos comerciales', 'Kit de lanzamiento'],
  },
];

export const cases: CaseStudy[] = [
  {
    index: 'A-01',
    sector: 'Real estate',
    title: 'Un edificio que se recorre antes de existir.',
    city: 'Puerto Madero, CABA',
    concept: 'Lanzamiento ficticio con tour atmosférico, selector de unidades y agenda de visitas integrada.',
    signals: ['Tour 3D', 'Unidades', 'Agenda'],
    color: '#d8ff3e',
  },
  {
    index: 'A-02',
    sector: 'Hospitality',
    title: 'La reserva empieza mucho antes de elegir una mesa.',
    city: 'Palermo, CABA',
    concept: 'Micrositio ficticio que convierte carta, arquitectura y ritual de servicio en una experiencia editorial.',
    signals: ['Menú vivo', 'Reservas', 'Eventos'],
    color: '#ff7a45',
  },
  {
    index: 'A-03',
    sector: 'Wellness',
    title: 'Un sistema sereno para una operación compleja.',
    city: 'Córdoba Capital',
    concept: 'Experiencia ficticia con diagnóstico guiado, membresías y agenda por sede, disciplina y profesional.',
    signals: ['Diagnóstico', 'Membresías', 'Agenda'],
    color: '#69d9ff',
  },
  {
    index: 'A-04',
    sector: 'Retail',
    title: 'Un catálogo que se siente como una campaña.',
    city: 'Rosario, Santa Fe',
    concept: 'Lanzamiento ficticio de colección con navegación gestual, combinaciones y consultas directas.',
    signals: ['Colección', 'Looks', 'WhatsApp'],
    color: '#b597ff',
  },
];

export const testimonials: Testimonial[] = [
  {
    quote: 'No queríamos “una web linda”. Queríamos que la propuesta se entendiera antes de la primera reunión. El enfoque cambió por completo la conversación comercial.',
    name: 'Julieta Bianchi',
    role: 'Directora, Estudio Bianchi Arquitectura',
    city: 'Buenos Aires',
  },
  {
    quote: 'La experiencia logró algo difícil: transmitir energía sin perder sofisticación. Cada movimiento tenía una función y el sistema de consultas nos ordenó la demanda.',
    name: 'Martín Arce',
    role: 'Socio, Casa Umbral Gastronomía',
    city: 'CABA',
  },
  {
    quote: 'El configurador hizo que llegaran consultas mucho más claras. La gente ya entendía el servicio, el rango y cuál era el próximo paso.',
    name: 'Sofía Rinaldi',
    role: 'Fundadora, Senda Wellness',
    city: 'Córdoba',
  },
];

export const faqs = [
  ['¿La animación hace más lento el sitio?', 'No por definición. Diseñamos presupuestos de rendimiento por dispositivo, cargamos lo complejo de forma progresiva y reducimos efectos donde no agregan valor.'],
  ['¿Se puede conectar con herramientas existentes?', 'Sí. El frontend queda preparado para sumar CRM, analytics, reservas, ecommerce o automatizaciones. Las integraciones se cotizan según alcance.'],
  ['¿Trabajan fuera de Buenos Aires?', 'Sí. El proceso es remoto y trabajamos con equipos en CABA, GBA, Córdoba, Rosario, Mendoza y todo el país.'],
  ['¿Qué necesitan para empezar?', 'Una decisión de negocio concreta, acceso a los responsables y material de marca disponible. Si falta definición, la primera etapa la construye.'],
];

export const businessOptions = ['Gastronomía / hospitality', 'Real estate', 'Wellness', 'Retail / ecommerce', 'Servicios profesionales', 'Eventos', 'Otro'];
export const objectiveOptions = ['Generar consultas calificadas', 'Lanzar algo nuevo', 'Elevar percepción de marca', 'Vender o reservar online', 'Explicar una propuesta compleja'];
export const impactOptions = ['Editorial', 'Cinemático', 'Inmersivo'];
export const featureOptions = ['3D / WebGL', 'Cotizador', 'Reservas', 'Catálogo', 'Dashboard', 'Automatizaciones'];
export const timelineOptions = ['Menos de 6 semanas', '6 a 10 semanas', '10 a 16 semanas', 'Flexible'];
export const budgetOptions = ['ARS 2M — 5M', 'ARS 5M — 9M', 'ARS 9M — 15M', 'Más de ARS 15M'];
